/**
 * The evaluator's Experiments tab (as Langfuse lists an evaluator's runs): its
 * score over time, then every experiment that used it — newest first, live
 * ones polling — with an All / Agent runs / Dataset outputs filter kept in the
 * URL (`?show=`). A row opens the experiment.
 */

import { QueryState } from "@/components/ui/states";
import { Segmented } from "@/components/ui/tabs";
import { useUrlState } from "@/lib/use-url-state";
import { useEvaluatorRuns } from "./hooks";
import { RunsChart } from "./runs-chart";
import { RunsTable } from "./runs-table";
import type { RunsFilter } from "./types";

const FILTERS: { key: RunsFilter; label: string; param: string | null }[] = [
  { key: "all", label: "All", param: null },
  { key: "agent", label: "Agent runs", param: "agent" },
  { key: "dataset_output", label: "Dataset outputs", param: "outputs" },
];

export function RunsTab({ evaluatorKey, evaluatorName }: { evaluatorKey: string; evaluatorName: string }) {
  const url = useUrlState();
  const show = FILTERS.find((f) => f.param === url.get("show"))?.key ?? "all";
  const all = useEvaluatorRuns(evaluatorKey, "all");
  const runs = useEvaluatorRuns(evaluatorKey, show);

  return (
    <div className="flex flex-col gap-4">
      <RunsChart runs={all.data ?? []} name={evaluatorName} />
      <Segmented
        label="Show experiments"
        value={show}
        onChange={(key) => url.set({ show: FILTERS.find((f) => f.key === key)?.param ?? null })}
        items={FILTERS.map(({ key, label }) => ({ key, label }))}
      />
      <QueryState query={runs} what="the experiments">
        <RunsTable runs={runs.data ?? []} />
      </QueryState>
    </div>
  );
}
